import React, { useState } from "react";
import dayjs from "dayjs";
import { collection, addDoc, getFirestore } from "firebase/firestore";
import { Box, Typography, Button } from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import SquareCardForm from "../payments/SquareCardForm";

export default function ConfirmBookingPanel({
  bookingData,
  onCancel,
  onSuccess,
  memberData,
  adultPrice30,
  adultPrice60,
  juniorPrice30,
  juniorPrice60,
}) {
  console.log("ConfirmBookingPanel => bookingData:", bookingData);

  const db = getFirestore();
  const createSquarePayment = httpsCallable(functions, "createSquarePayment");
  const setPasscodeForBookingCall = httpsCallable(functions, "setPasscodeForBookingCall");

  const [isPaid, setIsPaid] = useState(false);

  const hasActive247Membership = check247Active(memberData);

  const isJunior = checkIfJunior(memberData);

  const bookingDurationMins = calculateDuration(
    bookingData.startTime,
    bookingData.endTime
  );
  const blockCount = bookingDurationMins / 30;

  let costInDollars = 0;
  let rateTypeLabel = "24/7 Member (No Charge)";

  if (!hasActive247Membership) {
    rateTypeLabel = isJunior ? "Junior Rate" : "Adult Rate";
    const pricePer30 = isJunior ? juniorPrice30 : adultPrice30;
    costInDollars = blockCount * pricePer30;
  }

  async function handlePayment(nonce) {
    if (costInDollars <= 0) {
      await createPaidBooking(db, bookingData, memberData, setPasscodeForBookingCall);
      setIsPaid(true);
      alert("No charge. Booking confirmed!");
      if (onSuccess) onSuccess();
      return;
    }

    try {
      const amountInCents = costInDollars * 100;
      const paymentResponse = await createSquarePayment({
        cardNonce: nonce,
        amount: amountInCents,
        locationId: "L7CGQXSP8NS44",
        note: `Court Booking on ${bookingData.date} (${bookingData.startTime}-${bookingData.endTime})`,
        customerId: memberData?.squareCustomerId,
      });

      if (!paymentResponse.data.success) {
        alert(paymentResponse.data.message || "Payment failed");
        return;
      }

      await createPaidBooking(db, bookingData, memberData, setPasscodeForBookingCall);
      setIsPaid(true);
      alert("Payment successful! Booking confirmed.");
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Payment or doc creation error =>", err);
      alert(err.message);
    }
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Confirm Booking &amp; Pay
      </Typography>

      <Typography>Date: {bookingData.date}</Typography>
      <Typography>
        Time: {bookingData.startTime} - {bookingData.endTime}
      </Typography>
      <Typography>Courts: {bookingData.courtsUsed}</Typography>
      <Typography>
        Members: {bookingData.members ? bookingData.members.length : 0}
      </Typography>

      <Typography sx={{ mt: 2 }}>{rateTypeLabel}</Typography>
      <Typography sx={{ mt: 1 }}>
        Booking Cost: <strong>${costInDollars.toFixed(2)}</strong>
      </Typography>

      {!isPaid && (
        <Box sx={{ mt: 3 }}>
          {costInDollars > 0 ? (
            <>
              <Typography sx={{ mb: 2 }}>Enter Card Details:</Typography>
              <SquareCardForm
                applicationId="sq0idp-vssGW3fD2LCsWJgBa4U6tg"
                locationId="L7CGQXSP8NS44"
                onNonce={(nonce) => handlePayment(nonce)}
                label="Pay Now"
              />
            </>
          ) : (
            <Typography sx={{ mb: 2 }}>
              No charge for 24/7 membership. Click confirm to finalize booking.
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" color="secondary" onClick={onCancel} sx={{ mr: 2 }}>
              Cancel
            </Button>

            {costInDollars <= 0 && (
              <Button variant="contained" color="primary" onClick={() => handlePayment(null)}>
                Confirm Booking
              </Button>
            )}
          </Box>
        </Box>
      )}

      {isPaid && (
        <Typography sx={{ mt: 2, color: "green" }}>
          Booking confirmed!
        </Typography>
      )}
    </Box>
  );
}

function check247Active(memberData) {
  if (!memberData) return false;

  const membershipStr = (memberData.membershipType || "").toLowerCase();
  const membershipTypeHas247 =
    membershipStr.includes("24/7") && !membershipStr.includes("Not 24/7");

  const activeSubscriptionHas247 =
    memberData.subscriptions &&
    memberData.subscriptions.some((sub) => {
      const status = (sub.status || "").toLowerCase();
      const planName = (sub.planName || "").toLowerCase();
      return status === "active" && planName.includes("24/7") && !planName.includes("not 24/7");
    });

  return membershipTypeHas247 || activeSubscriptionHas247;
}



function checkIfJunior(memberData) {
  if (!memberData?.birthday) return false;
  const birthday = dayjs(memberData.birthday, "DD/MM/YYYY");
  if (!birthday.isValid()) return false;
  const age = dayjs().diff(birthday, "year");
  return age < 18;
}

function calculateDuration(startTime, endTime) {
  const [sH, sM] = (startTime || "00:00").split(":");
  const [eH, eM] = (endTime || "00:00").split(":");
  const startMin = parseInt(sH, 10) * 60 + parseInt(sM, 10);
  const endMin = parseInt(eH, 10) * 60 + parseInt(eM, 10);
  return endMin - startMin;
}

async function createPaidBooking(db, bookingData, memberData, setPasscodeForBookingCall) {
  const passcodeRequired = !memberData?.cardNumber;
  const docRef = await addDoc(collection(db, "Bookings"), {
    date: bookingData.date,
    startTime: bookingData.startTime,
    endTime: bookingData.endTime,
    courtsUsed: bookingData.courtsUsed,
    members: bookingData.members.map((m) => m.id),
    paid: true,
    paymentAt: new Date().toISOString(),
    ...(passcodeRequired && { passcodeRequired: true }),
  });

  if (passcodeRequired) {
    const now = dayjs();
    const startMoment = dayjs(`${bookingData.date} ${bookingData.startTime}`, "YYYY-MM-DD HH:mm");
    const diffMins = startMoment.diff(now, "minute");

    if (diffMins < 15) {
      console.log("Booking <15 mins away => calling setPasscodeForBookingCall immediately.");
      try {
        const resp = await setPasscodeForBookingCall({ bookingId: docRef.id });
        console.log("Immediate passcode response =>", resp.data);
      } catch (err) {
        console.error("Immediate passcode call failed =>", err);
      }
    }
  }
}
